export const RULEBOOK_SECTIONS = [
  {
    id: "introduction",
    number: "1.0",
    title: "Introduction",
    blocks: [
      { type: "heading", text: "What is Aeterna Invicta?" },
      "Aeterna Invicta strives to combine the best and most interesting aspects of Magic the Gathering and Path of Exile into a collectible card game. Build Decks, discover synergies, and play your cards right as you try to best your opponent.",
      "*Become eternal and uncontested!*",
      { type: "heading", text: "Using This Rulebook" },
      "This rulebook explains how a duel works, from your first draw to the final blow. Use the Table of Contents to jump to any section. If a card's text ever disagrees with these rules, the card's text takes priority. For quick definitions of keywords such as Expend, Encore, or Burn, see the **Glossary** in the Library.",
    ],
  },
  {
    id: "howToWin",
    number: "1.1",
    title: "How to Win",
    blocks: [
      "Aeterna Invicta is a one-on-one duel. Both champions begin at **10 Health**. Reduce your opponent's Health to **0** to win the match.",
      {
        type: "list",
        items: [
          "**Combat.** Damage from equipment, abilities, and ailments such as Burn and Poison all counts.",
          "**Forfeit.** A player who forfeits loses immediately.",
          "**Timeout.** In timed matches, running out of time on 3 turns loses the match (see 7.2).",
          "**Overtime.** Long matches deal escalating damage to both players so every duel reaches an end (see 7.1).",
        ],
      },
    ],
  },
  {
    id: "cards",
    number: "1.2",
    title: "The Cards",
    blocks: [
      "Every deck is built from three kinds of cards.",
      {
        type: "list",
        items: [
          "**Aspect.** Your class. Each deck has exactly one. It starts in play, applies its passive effects for the whole match, and decides which Traits you can choose when you level up.",
          "**Equipment.** Weapons, armor, and trinkets. Equipment is played onto your board, where it can be expended for resources or for its activated effect.",
          {
            text: "**Ability.** Skills and spells slotted into the ability columns above your equipment.",
            items: [
              "**Active** abilities are activated by paying their cost.",
              "**Augments** sit in the linked slots of a column and modify matching Active abilities.",
              "**Essences** enhance the Inherent effects of their column's equipment while in the host slot. In a linked slot they are inactive.",
            ],
          },
        ],
      },
      "Each card also has a **Power** rating used for deck building (see 6.2) and a set symbol showing which expansion it belongs to.",
    ],
  },
  {
    id: "attributes",
    number: "1.3",
    title: "Attributes",
    blocks: [
      "Cards belong to one or more attributes. Equipment usually produces resources of its attribute, and abilities usually cost them.",
      {
        type: "list",
        items: [
          "**Strength.** Power tempered by endurance. Heavy physical attacks, Bleed, and Physical Armor.",
          "**Dexterity.** Speed sharpened into opportunity. Rapid attacks, Dodge, Poison, and precise effects that bypass armor.",
          "**Wisdom.** Arcane mastery. Magical and elemental damage, healing, Magic Armor, and effects that strengthen or repeat abilities.",
          "**Neutral.** Versatility without allegiance. Attacks, healing, and protection useful in any build.",
        ],
      },
      "Strength, Dexterity, and Wisdom are the **primary** attributes. A Neutral cost can be paid with any resource, but a primary cost (for example, 2 Wisdom) can only be paid with that attribute.",
    ],
  },
  {
    id: "startingMatch",
    number: "2.0",
    title: "Starting a Match",
    blocks: [
      "Your Equipment and Ability decks are shuffled together into a single 20-card deck. Your Aspect does not go into the deck; it starts the match in play.",
    ],
  },
  {
    id: "openingHand",
    number: "2.1",
    title: "Opening Hand and Mulligan",
    blocks: [
      {
        type: "list",
        items: [
          "Draw up to your hand size of **4** cards.",
          "If a weapon with **Lone Weapon** is the only weapon in your deck, it always starts in your opening hand.",
          "You may **Mulligan** once: shuffle your hand back into your deck and draw a fresh hand of 4.",
          "When you are happy with your hand, choose **Ready**. In timed matches you have 60 seconds to decide.",
        ],
      },
    ],
  },
  {
    id: "turnOrder",
    number: "2.2",
    title: "Turn Order",
    blocks: [
      "A random player goes first. To balance that advantage, the player going second starts with **1 XP**. Players then alternate turns. A **round** is complete once both players have taken a turn.",
    ],
  },
  {
    id: "gameplay",
    number: "3.0",
    title: "Gameplay",
    blocks: [
      "Each turn has three parts: the start of turn, your main phase, and the end of turn. Most of the game happens in your main phase, where you play cards, gather resources, and attack.",
    ],
  },
  {
    id: "turnStructure",
    number: "3.1",
    title: "Turn Structure",
    blocks: [
      { type: "heading", text: "Start of Turn" },
      "These happen automatically, in order:",
      {
        type: "list",
        ordered: true,
        items: [
          "Your remaining Thorns stacks clear.",
          "Your elemental Resistances remove matching Burn, Chill, and Shock stacks.",
          "Regeneration heals you, then Burn, Poison, and Wither deal their damage.",
          "Your equipment becomes unexpended and your slotted abilities can be activated again.",
          "Your resource pool empties. Persistent resources remain.",
          "Aspect and equipment start-of-turn effects resolve.",
          "Draw 1 card, plus any Restock. You cannot draw past your hand size or from an empty deck.",
        ],
      },
      { type: "heading", text: "Main Phase" },
      "Take any number of actions in any order, within the limits described in 3.2 and 3.3. End your turn when you are done.",
      { type: "heading", text: "End of Turn" },
      {
        type: "list",
        items: [
          "Unspent resources are lost. Persistent resources remain.",
          "Your Combo ends.",
          "You lose 1 Bleed, 1 Chill, and 1 Shock stack.",
          "Charge kinds that clear at end of turn, such as Earth Charges, are removed. Other charge kinds stay until spent.",
          "Your opponent's temporary defenses (temporary armor, Dodge, temporary Resistances, and Thorns) expire. Temporary defenses you gain on your turn protect you through your opponent's next turn.",
        ],
      },
    ],
  },
  {
    id: "playingCards",
    number: "3.2",
    title: "Playing Cards",
    blocks: [
      { type: "heading", text: "Equip" },
      {
        type: "list",
        items: [
          "You may play **one** Equipment card from your hand each turn.",
          {
            text: "It goes to the board slot that matches its type:",
            items: [
              "**Weapon:** one-handed and two-handed weapons.",
              "**Armor:** off-hands, head, body, hands, and feet.",
              "**Trinket:** trinkets.",
            ],
          },
          "The slot must be empty. To make room, discard the equipment currently in that slot first.",
          "Newly equipped gear cannot be expended until your next turn, unless it has **Fast Hands** or **Swift Feet**.",
          "**On Equip** effects resolve as soon as the card enters play.",
        ],
      },
      { type: "heading", text: "Slot an Ability" },
      {
        type: "list",
        items: [
          "You may slot **one** Ability card from your hand each turn. Effects like **Nimble Fingers** let you slot more.",
          "Each board slot (Weapon, Armor, and Trinket) has an ability column with a **host slot** on top and **3 linked slots** beneath it.",
          "The host slot accepts an **Active** or an **Essence**. Linked slots accept **Augments**, Actives, or Essences, but Actives can only be activated, and Essences only take effect, from the host slot.",
          "Augments modify the matching Active ability in their column.",
          "Slotting into an occupied slot discards the ability already there.",
          "Slotted abilities cannot return to your hand, but you can move one to another slot. Moving uses your ability play for the turn.",
          "**On Slot** effects resolve when the ability is slotted.",
        ],
      },
    ],
  },
  {
    id: "actions",
    number: "3.3",
    title: "Actions",
    blocks: [
      "During your main phase you may take these actions as often as their limits allow:",
      {
        type: "list",
        items: [
          "**Expend for resources.** Expend an equipment to gain its resources. Each equipment can be expended once per turn.",
          "**Expend for an effect.** Instead of resources, expend the equipment to use its activated effect, such as a weapon attack. Some effects have an extra cost.",
          "**Activate an ability.** Pay an Active ability's cost to use it. Each slotted ability can be activated once per turn unless it has **Encore**.",
          "**Invoke.** Once per turn, discard a card with an Invoke effect from your hand to resolve that effect. Cards cannot be discarded from your hand any other way.",
          "**Equipment actions.** Some equipment offers extra actions that do not expend it, such as **Refresh**, **Repair**, **Hone**, **Sharpen**, **Fletch**, or **Add Charge**. Pay the listed cost to use them.",
          "**Discard from the board.** Discard an equipped card or a slotted ability to free its slot.",
          "**End turn.** Pass the turn to your opponent.",
        ],
      },
      { type: "heading", text: "Combo" },
      "Using the same damaging effect or ability repeatedly in a streak builds a **Combo**. A card with **Combo X** deals +X damage and costs +X of its attribute for each step of the streak. The number of steps is capped by that ability's current level, including temporary level boosts from Augments or equipment: a level 1 ability caps at +X, a level 2 ability at +2X, and so on. Equipment combos cap at one step. Using a different card, equipping, slotting, or discarding breaks the streak, and every Combo ends with your turn.",
    ],
  },
  {
    id: "resources",
    number: "3.4",
    title: "Resources",
    blocks: [
      {
        type: "list",
        items: [
          "Gain resources by expending equipment for resources, or from effects that grant them.",
          "Resources go into your pool and stay there until you spend them or your turn ends.",
          "A Neutral cost can be paid with any resource. A Strength, Dexterity, or Wisdom cost must be paid with that attribute; Neutral cannot pay it.",
          "**Persistent** resources, marked with *, carry over between turns until spent. Normal resources of the same attribute are spent first.",
          "**Shock** reduces the resources you gain, and **Chill** makes your Active abilities cost 1 extra Neutral.",
        ],
      },
    ],
  },
  {
    id: "combat",
    number: "4.0",
    title: "Combat",
    blocks: [
      "Attacks come from expending weapons and other equipment, activating abilities, and ailments that deal damage over time. Your defenses decide how much of that damage actually reaches your Health.",
    ],
  },
  {
    id: "damage",
    number: "4.1",
    title: "Damage and Mitigation",
    blocks: [
      "Every hit has a damage type that decides what reduces it:",
      {
        type: "list",
        items: [
          "**Physical:** reduced by Physical Armor.",
          "**Magical:** reduced by Magic Armor.",
          "**Fire, Cold, and Lightning:** reduced by the matching Resistance.",
        ],
      },
      "Armor reduces a matching hit by your current armor total, then that hit removes 1 armor stack. Damage that **Ignores Armor** skips armor entirely and does not remove stacks.",
      "**Dodge:** each Dodge stack reduces the next hit by 25% (rounded down) and is then removed. A hit that would deal at least 1 damage still deals at least 1.",
      "**Melee**, **Ranged**, and **Magic** are card tags, not damage types. They matter for interactions such as Hone, Fletch, and many Aspect bonuses, but a melee or ranged physical attack still deals physical damage.",
      "If you control **Minions**, incoming damage hits your oldest minion first.",
    ],
  },
  {
    id: "ailments",
    number: "4.2",
    title: "Ailments and Stacks",
    blocks: [
      {
        type: "list",
        items: [
          "**Burn:** damages you at the start of your turn. Each tick deals 1 damage and removes 1 stack.",
          "**Poison:** damages you at the start of your turn. Each tick deals 1 damage and removes 1 stack.",
          "**Bleed:** damages you whenever you expend equipment. Each tick deals 1 damage and removes 1 stack. You also lose 1 Bleed at the end of your turn.",
          "**Chill:** while you have any Chill, your Active abilities cost 1 extra Neutral. Using an ability removes 1 stack, and 1 more is removed at the end of your turn.",
          "**Shock:** while you have any Shock, the next equipment you expend for resources gives 1 less. Expending for resources removes 1 stack, and 1 more is removed at the end of your turn.",
          "**Wither:** at the start of your turn, lose current and maximum Health equal to your Wither stacks. At the end of your turn, lose 1 stack and restore 1 maximum Health.",
          "**Thorns:** when you take physical attack damage, the attacker takes 1 damage and you lose 1 Thorns. Remaining stacks clear at the start of your turn.",
          "**Regeneration:** heals you at the start of your turn, before Burn and Poison. Each tick heals 1 and removes 1 stack.",
        ],
      },
      "Elemental Resistances also cleanse ailments. At the start of your turn, Fire Resistance removes Burn, Cold Resistance removes Chill, and Lightning Resistance removes Shock, up to your Resistance value.",
    ],
  },
  {
    id: "healing",
    number: "4.3",
    title: "Healing and Health",
    blocks: [
      "Healing restores Health up to your maximum. Effects that raise your **maximum Health** also raise your current Health by the same amount. Equipment that grants maximum Health while equipped takes that bonus away when it leaves play, without healing you.",
    ],
  },
  {
    id: "progression",
    number: "5.0",
    title: "Leveling Up",
    blocks: [
      "Every match starts both champions at level 1. Complete Quests during the match to earn XP, level up, gain Health, and choose Traits from your Aspect.",
    ],
  },
  {
    id: "xpQuests",
    number: "5.1",
    title: "XP and Quests",
    blocks: [
      "Open **Quests** under your XP bar to track your progress. Quests can be completed in any order.",
      { type: "heading", text: "Every Turn (+1 XP each)" },
      {
        type: "list",
        items: [
          "Play an equipment.",
          "Slot an ability.",
          "Expend equipment for resources.",
          "Deal combat damage to your opponent.",
          "Heal yourself or gain a defensive buff.",
        ],
      },
      { type: "heading", text: "Once Per Match (+3 XP each)" },
      {
        type: "list",
        items: [
          "Fill all three equipment slots.",
          "Fill the host slot of every ability column.",
          "Equip every type of equipment: weapon, head, body, hands, feet, and trinket.",
          "Activate an Active ability modified by a matching Augment.",
          "Activate a level 2 or higher ability.",
        ],
      },
    ],
  },
  {
    id: "levels",
    number: "5.2",
    title: "Levels and Traits",
    blocks: [
      "Fill your XP bar to level up. Your XP resets after each level. Each level adds Health to both your current and maximum Health, and offers a **Trait** choice from your Aspect.",
      {
        type: "list",
        items: [
          "**Level 1:** start of match, 10 Health.",
          "**Level 2:** 5 XP, +5 Health (15 maximum).",
          "**Level 3:** 10 XP, +10 Health (25 maximum).",
          "**Level 4:** 15 XP, +10 Health (35 maximum).",
          "**Level 5:** 20 XP, +10 Health (45 maximum). This is the highest level.",
        ],
      },
      "Traits follow your Aspect's attribute tree. For example, Fighter Traits follow Strength. Higher levels unlock stronger options.",
    ],
  },
  {
    id: "abilityLevels",
    number: "5.3",
    title: "Ability Levels",
    blocks: [
      "Abilities start at level 1. Card effects such as **Research** can raise an ability's level up to 4. Temporary bonuses from Augments or equipment can push an ability's effective level even higher for the match. Higher levels strengthen an ability and raise its Combo cap.",
    ],
  },
  {
    id: "deckBuilding",
    number: "6.0",
    title: "Deck Building",
    blocks: [
      "Before you duel, build a deck from the cards in your Collection. A complete deck is really three decks: one Aspect, ten Equipment, and ten Abilities.",
    ],
  },
  {
    id: "deckRules",
    number: "6.1",
    title: "Deck Rules",
    blocks: [
      {
        type: "list",
        items: [
          "**Aspect deck:** exactly 1 Aspect.",
          "**Equipment deck:** exactly 10 Equipment cards.",
          "**Ability deck:** exactly 10 Ability cards.",
          "No more than **2 copies** of any card.",
          "A combined total of **10 Power or less**.",
        ],
      },
      "During a match, the Equipment and Ability decks are shuffled together into one 20-card deck.",
    ],
  },
  {
    id: "power",
    number: "6.2",
    title: "Power",
    blocks: [
      "Every card has a Power rating that reflects how strong it is. Your whole deck, Aspect included, must total **10 Power or less**. Many dependable cards cost 0 Power, so spend your budget on the few powerful cards that define your strategy.",
    ],
  },
  {
    id: "deckTips",
    number: "6.3",
    title: "Deck Building Tips",
    blocks: [
      {
        type: "list",
        items: [
          "**Match your attributes.** Your equipment produces the resources your abilities spend. A deck of Wisdom abilities needs Wisdom equipment.",
          "**Cover all three slots.** Include weapons, armor, and trinkets so every column has gear and an ability.",
          "**Pair Actives with Augments.** Augments only modify matching Actives in the same column.",
          "**Play to your Aspect.** Its passive effects and Traits reward a particular style, so build around them.",
          "**Keep it lean.** With a 4-card hand and 1 draw per turn, cheap cards keep you active early.",
          "**Borrow ideas.** Browse the sample **Decks** in the Library for inspiration.",
        ],
      },
    ],
  },
  {
    id: "advanced",
    number: "7.0",
    title: "Advanced Rules",
    blocks: [
      "These rules cover match timing and special cards you will meet as your collection grows.",
    ],
  },
  {
    id: "overtime",
    number: "7.1",
    title: "Overtime",
    blocks: [
      "Beginning with the second player's turn in round 20, each player loses Health at the end of their turn: 1 in round 20, 2 in round 21, 3 in round 22, and so on. Armor and minions do not prevent Overtime damage.",
    ],
  },
  {
    id: "turnTimer",
    number: "7.2",
    title: "Turn Timer",
    blocks: [
      "Matches against other players are timed. You start with 90 seconds. Ending your turn banks your remaining time plus 15 seconds for your next turn. If your clock runs out, your turn ends and your clock resets to 60 seconds. Running out of time 3 times loses the match.",
      "Matches against the CPU, tutorials, and adventures are not timed.",
    ],
  },
  {
    id: "specialCards",
    number: "7.3",
    title: "Tokens, Minions, and Auras",
    blocks: [
      {
        type: "list",
        items: [
          "**Tokens** are temporary equipment created by an Aspect. They are free to play, and when they leave your hand or board they leave play instead of going to your deck or discard pile. Tokens cannot be Invoked.",
          "**Minions** are summoned by Active abilities into the equipment slot where they are played. While a minion is active, that equipment is replaced by the minion and cannot use its own effects. Expend the minion for its action, or Unsummon it. A minion at 0 Health is destroyed.",
          "**Auras** are created by Active abilities on the equipment slot where they are played. While active, that equipment is replaced by the Aura and cannot use its own effects. The Aura provides ongoing benefits until you deactivate it, and deactivating does not refund its cost.",
        ],
      },
    ],
  },
  {
    id: "glossary",
    number: "7.4",
    title: "Keywords",
    blocks: [
      "Cards use many keywords, such as Encore, Echo, Sharpen, and Hex. You can hover or tap a keyword on a card for a quick explanation, or search every keyword in the **Glossary** in the Library.",
    ],
  },
];
